/**
 * Server-Sent Events (SSE) Service for real-time notifications
 */

interface SSEOptions {
  onMessage?: (event: MessageEvent) => void;
  onError?: (error: Event) => void;
  onOpen?: (event: Event) => void;
  onReconnect?: () => void;
  reconnectInterval?: number;
}

class SSEService {
  private eventSource: EventSource | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private isReconnecting = false;
  private url: string = '';
  private options: SSEOptions = {};

  /**
   * Connect to SSE endpoint
   */
  connect(url: string, token: string, options: SSEOptions = {}) {
    this.url = url;
    this.options = options;

    // Close existing connection
    this.disconnect();

    console.log('[SSE] Connecting to:', url);

    // Create EventSource with auth header
    this.eventSource = new EventSource(`${url}?token=${encodeURIComponent(token)}`);

    // Handle connection open
    this.eventSource.onopen = (event) => {
      console.log('[SSE] Connection opened');
      this.isReconnecting = false;
      this.clearReconnectTimer();
      this.options.onOpen?.(event);
    };

    // Handle messages
    this.eventSource.onmessage = (event) => {
      console.log('[SSE] Message received:', event);
      this.options.onMessage?.(event);
    };

    // Handle specific event types
    this.eventSource.addEventListener('connected', (event) => {
      console.log('[SSE] Connected event:', event);
    });

    this.eventSource.addEventListener('event', (event: MessageEvent) => {
      console.log('[SSE] Event received:', event);
      try {
        const data = JSON.parse(event.data);
        this.handleEvent('event', data);
      } catch (error) {
        console.error('[SSE] Error parsing event data:', error);
      }
    });

    this.eventSource.addEventListener('customer-request', (event: MessageEvent) => {
      console.log('[SSE] Customer request received:', event);
      try {
        const data = JSON.parse(event.data);
        this.handleEvent('customer-request', data);
      } catch (error) {
        console.error('[SSE] Error parsing customer request data:', error);
      }
    });

    this.eventSource.addEventListener('ping', (event: MessageEvent) => {
      console.log('[SSE] Keep-alive ping received');
    });

    // Handle errors
    this.eventSource.onerror = (error) => {
      console.error('[SSE] Connection error:', error);
      this.options.onError?.(error);

      // Attempt reconnection
      if (this.eventSource?.readyState === EventSource.CLOSED) {
        this.scheduleReconnect();
      }
    };
  }

  /**
   * Handle specific event types
   */
  private handleEvent(type: string, data: any) {
    // Dispatch custom event for components to listen
    const customEvent = new CustomEvent(`sse-${type}`, {
      detail: data,
      bubbles: true,
      cancelable: true
    });
    document.dispatchEvent(customEvent);
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect() {
    if (this.isReconnecting) return;

    this.isReconnecting = true;
    const interval = this.options.reconnectInterval || 5000;

    console.log(`[SSE] Scheduling reconnection in ${interval}ms`);

    this.reconnectTimer = setTimeout(() => {
      console.log('[SSE] Attempting to reconnect...');
      this.options.onReconnect?.();
      
      // Get fresh token and reconnect
      const token = localStorage.getItem('token');
      if (token && this.url) {
        this.connect(this.url, token, this.options);
      }
    }, interval);
  }

  /**
   * Clear reconnection timer
   */
  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Disconnect from SSE
   */
  disconnect() {
    if (this.eventSource) {
      console.log('[SSE] Disconnecting...');
      this.eventSource.close();
      this.eventSource = null;
    }
    this.clearReconnectTimer();
    this.isReconnecting = false;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.eventSource?.readyState === EventSource.OPEN;
  }

  /**
   * Send keep-alive
   */
  async sendKeepAlive(token: string) {
    try {
      const response = await fetch('/api/notifications/sse/keepalive', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('[SSE] Keep-alive failed:', response.status);
      }
    } catch (error) {
      console.error('[SSE] Keep-alive error:', error);
    }
  }
}

// Create singleton instance
export const sseService = new SSEService();

/**
 * Hook for using SSE in components
 */
import { useEffect, useCallback } from 'react';

interface UseSSEOptions {
  endpoint: 'vendor' | 'table';
  id: number;
  onEvent?: (type: string, data: any) => void;
  onConnect?: () => void;
  onError?: (error: Event) => void;
  autoConnect?: boolean;
}

export const useSSE = ({
  endpoint,
  id,
  onEvent,
  onConnect,
  onError,
  autoConnect = true
}: UseSSEOptions) => {
  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('[useSSE] No auth token found');
      return;
    }

    const url = `/api/notifications/sse/${endpoint}/${id}/subscribe`;

    sseService.connect(url, token, {
      onOpen: () => {
        console.log('[useSSE] Connected successfully');
        onConnect?.();
      },
      onError: (error) => {
        console.error('[useSSE] Connection error:', error);
        onError?.(error);
      },
      onMessage: (event) => {
        console.log('[useSSE] Message received:', event);
      }
    });
  }, [endpoint, id, onConnect, onError]);

  // Set up event listeners
  useEffect(() => {
    if (!onEvent) return;

    const handleSSEEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const type = event.type.replace('sse-', '');
      onEvent(type, customEvent.detail);
    };

    // Listen for SSE events
    document.addEventListener('sse-event', handleSSEEvent);
    document.addEventListener('sse-customer-request', handleSSEEvent);

    return () => {
      document.removeEventListener('sse-event', handleSSEEvent);
      document.removeEventListener('sse-customer-request', handleSSEEvent);
    };
  }, [onEvent]);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      sseService.disconnect();
    };
  }, [autoConnect, connect]);

  // Keep-alive interval
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const keepAliveInterval = setInterval(() => {
      if (sseService.isConnected()) {
        sseService.sendKeepAlive(token);
      }
    }, 60000); // Every minute

    return () => clearInterval(keepAliveInterval);
  }, []);

  return {
    connect,
    disconnect: () => sseService.disconnect(),
    isConnected: sseService.isConnected()
  };
};